import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Divider,
  message,
  Switch,
} from 'antd';
import {
  PiBellFill,
  PiChatsCircleFill,
  PiCompassFill,
  PiStopCircleFill,
} from 'react-icons/pi';

import CommunityAPI from '../../../api/CommunityAPI';
import CommunityMessagesAPI from '../../../api/CommunityMessageAPI';
import { SocketAPI } from '../../../api/SocketAPI';
import Header from '../../../components/Header/Header';
import {
  setActiveUserInSelectedRoom,
  updateChatRoomSettings,
} from '../../../context/communityMessageReducer';
import { updateChannelSettings } from '../../../context/communityReducer';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../shared/hooks';
import { getDMUser } from '../../../shared/utils';
import { SOCKET_ACTION_EVENTS } from '../../../types/socketTypes';
import CommunityChannelMemberList from './CommunityChannelMemberList';

type Props = {
  isChatRoomInfo: boolean;
};

const CommunityChannelInfo: React.FC<Props> = ({ isChatRoomInfo }) => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const { selectedChannelData } = useAppSelector((state) => state.community);
  const userDetails = useAppSelector((state) => state.user);
  const { chatRoomDetails, currentChannelActiveUser } = useAppSelector(
    (state) => state?.communityMessage,
  );

  const [isRemoving, setIsRemoveing] = useState(false);

  const infoPageDetails = useMemo(() => {
    if (isChatRoomInfo) {
      return chatRoomDetails?.channel;
    }
    return selectedChannelData;
  }, [chatRoomDetails, isChatRoomInfo, selectedChannelData]);

  const dmUser = useMemo(
    () =>
      chatRoomDetails?.roomType === 'dm' && chatRoomDetails?._id
        ? getDMUser(chatRoomDetails)
        : null,
    [chatRoomDetails],
  );

  const randerAvatar = useMemo(() => {
    if (chatRoomDetails?.roomType === 'dm') {
      return (
        <img
          src={
            dmUser?.profilePicUrl ||
            'https://tagmango.com/staticassets/avatar-placeholder.png-1612857612139.png'
          }
          alt=""
        />
      );
    }
    if (
      infoPageDetails?.communityProfilePicture &&
      infoPageDetails?.communityProfilePictureType === 'emoji'
    ) {
      return (
        <div className="channel-info_container_scroll_logo_emoji">
          {infoPageDetails?.communityProfilePicture}
        </div>
      );
    }
    return <img src={infoPageDetails?.communityProfilePicture} alt="logo" />;
  }, [
    chatRoomDetails?.roomType,
    dmUser?.profilePicUrl,
    infoPageDetails?.communityProfilePicture,
    infoPageDetails?.communityProfilePictureType,
  ]);

  const handleBack = () => {
    setIsRemoveing(true);
    setTimeout(() => {
      navigate.goBack();
    }, 200);
  };

  const joinRoom = useCallback(
    (room: string) => {
      SocketAPI.emit(SOCKET_ACTION_EVENTS.ROOM_JOIN, { room: room }, (resp) => {
        if (resp?.status === 'success') {
          dispatch(
            setActiveUserInSelectedRoom([
              ...(resp.result.activeParticipants || []),
            ]),
          );
        } else if (resp?.status === 'error') {
          message.error(resp?.message);
        }
      });
    },
    [dispatch],
  );

  const handleUpdateChannelSettings = useCallback(
    async (channelId: string, settings: any) => {
      const currentSettings = {
        feedMute: settings?.feedMuted || false,
        messageNotificationsMuted: settings?.messageNotificationsMuted || false,
      };

      dispatch(updateChannelSettings(channelId, settings));

      try {
        const resp = await CommunityAPI.updateUserSettings(channelId, settings);
        if (resp?.status !== 200) {
          dispatch(updateChannelSettings(channelId, currentSettings));
        }
      } catch (error) {
        showAppError(error);
        dispatch(updateChannelSettings(channelId, currentSettings));
      }
    },
    [dispatch],
  );

  const handleUpdateChatroomSettings = useCallback(
    async (roomId: string, settings: any) => {
      const currentSettings = {
        messageNotificationsMuted: settings?.messageNotificationsMuted || false,
      };
      let updatedSettings = settings;
      dispatch(updateChatRoomSettings(roomId, settings));

      if ('selfStopReceivingMessages' in settings) {
        updatedSettings = {
          stopReceivingMessages: settings?.selfStopReceivingMessages,
        };
      }

      try {
        const resp = await CommunityMessagesAPI.updateChatroomSettings(
          roomId,
          updatedSettings,
        );
        if (resp?.status !== 200) {
          dispatch(updateChatRoomSettings(roomId, currentSettings));
        }
      } catch (error) {
        showAppError(error);
        dispatch(updateChatRoomSettings(roomId, currentSettings));
      }
    },
    [dispatch],
  );

  const displaySettings = useCallback(() => {
    // console.log('chatRoomDetails', chatRoomDetails);
    if (chatRoomDetails?.roomType) {
      return (
        <div className="channel-info_container_scroll_settings">
          {chatRoomDetails?.roomType === 'dm' ? (
            <div className="channel-info_container_scroll_settings_item">
              <div className="channel-info_container_scroll_settings_item_logo">
                <PiStopCircleFill />
                <p className="channel-info_container_scroll_settings_item_logo_title">
                  Stop receiving messages
                </p>
              </div>
              <Switch
                checked={chatRoomDetails?.selfStopReceivingMessages || false}
                onChange={(checked) => {
                  handleUpdateChatroomSettings(chatRoomDetails?._id, {
                    selfStopReceivingMessages: checked,
                  });
                }}
              />
            </div>
          ) : null}
          {chatRoomDetails?.roomType === 'dm' ? <Divider /> : null}
          <div className="channel-info_container_scroll_settings_item">
            <div className="channel-info_container_scroll_settings_item_logo">
              <PiBellFill />
              <p className="channel-info_container_scroll_settings_item_logo_title">
                Mute notifications
              </p>
            </div>
            <Switch
              checked={chatRoomDetails?.messageNotificationsMuted || false}
              onChange={(checked) => {
                handleUpdateChatroomSettings(chatRoomDetails?._id, {
                  messageNotificationsMuted: checked,
                });
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="channel-info_container_scroll_settings">
        {infoPageDetails?.features.includes('feed') ? (
          <div className="channel-info_container_scroll_settings_item">
            <div className="channel-info_container_scroll_settings_item_logo">
              <PiCompassFill />
              <p className="channel-info_container_scroll_settings_item_logo_title">
                Mute feed
              </p>
            </div>
            <Switch
              checked={infoPageDetails?.feedMuted || false}
              onChange={(checked) => {
                handleUpdateChannelSettings(infoPageDetails?._id, {
                  feedMuted: checked,
                });
              }}
            />
          </div>
        ) : null}
        {infoPageDetails?.features?.length === 2 ? <Divider /> : null}
        {infoPageDetails?.features?.includes('messages') ? (
          <div className="channel-info_container_scroll_settings_item">
            <div className="channel-info_container_scroll_settings_item_logo">
              <PiChatsCircleFill />
              <p className="channel-info_container_scroll_settings_item_logo_title">
                Mute messages
              </p>
            </div>
            <Switch
              checked={infoPageDetails?.messageNotificationsMuted || false}
              onChange={(checked) => {
                handleUpdateChannelSettings(infoPageDetails?._id, {
                  messageNotificationsMuted: checked,
                });
              }}
            />
          </div>
        ) : null}
      </div>
    );
  }, [
    chatRoomDetails,
    handleUpdateChannelSettings,
    handleUpdateChatroomSettings,
    infoPageDetails?._id,
    infoPageDetails?.features,
    infoPageDetails?.feedMuted,
    infoPageDetails?.messageNotificationsMuted,
  ]);

  useEffect(() => {
    if (infoPageDetails && infoPageDetails?.chatRoom) {
      joinRoom(infoPageDetails?.chatRoom);
    }
  }, [infoPageDetails, joinRoom]);

  useEffect(() => {
    setIsRemoveing(false);
  }, []);

  return (
    <div
      className={`channel-info_container channel-info-${
        isRemoving ? 'remove' : 'show'
      }`}>
      <Header
        title={isChatRoomInfo ? 'Chat Info' : 'Channel Info'}
        handleBack={handleBack}
      />
      <div className="channel-info_container_scroll">
        <div className="channel-info_container_scroll_logo">{randerAvatar}</div>
        <p className="channel-info_container_scroll_channel-name">
          {dmUser?.name || infoPageDetails?.name}
        </p>
        {displaySettings()}
        <CommunityChannelMemberList
          onlineUserList={currentChannelActiveUser.filter(
            (eachUser) => eachUser?._id !== userDetails?.id,
          )}
          channelId={infoPageDetails?._id as string}
          isDMRoom={chatRoomDetails?.roomType === 'dm'}
          dmParticipentList={
            chatRoomDetails?.roomType === 'dm'
              ? chatRoomDetails?.participants
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default memo(CommunityChannelInfo);
